import { User, Role, RolePermissions, FeatureAccess } from '@/types/firebase';

/**
 * Permissions utility for checking user access rights
 * 
 * Simplified permission model:
 * 1. Menu-based permissions only - users either see a menu item or they don't
 * 2. If a user can see a menu item, they have full access to all features within it
 * 3. Admin users have access to everything except Tenants
 * 4. Super_admin users have access to everything including Tenants
 */

// Default permissions based on built-in roles 
export const defaultPermissions = {
  // Super_admin has access to everything, including Tenants
  super_admin: {
    dashboard: true,
    invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    customers: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    products: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    transactions: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    users: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    settings: true,
    virtualTerminal: true,
    donations: { canView: true, canCreate: true, canEdit: true, canDelete: true }
  },
  // Admin has access to everything except Tenants
  admin: {
    dashboard: true,
    invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    customers: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    products: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    transactions: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    users: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    settings: true,
    virtualTerminal: true,
    donations: { canView: true, canCreate: true, canEdit: true, canDelete: true }
  },
  // Manager has limited access
  manager: {
    dashboard: true,
    invoices: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    customers: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    products: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    transactions: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    users: { canView: true, canCreate: true, canEdit: true, canDelete: true },
    settings: false,
    virtualTerminal: true,
    donations: { canView: true, canCreate: true, canEdit: true, canDelete: true }
  },
  // User has basic access
  user: {
    dashboard: true,
    invoices: { canView: true, canCreate: true, canEdit: false, canDelete: false },
    customers: { canView: true, canCreate: true, canEdit: false, canDelete: false },
    products: { canView: true, canCreate: false, canEdit: false, canDelete: false },
    transactions: { canView: true, canCreate: true, canEdit: false, canDelete: false },
    users: { canView: false, canCreate: false, canEdit: false, canDelete: false },
    settings: false,
    virtualTerminal: false,
    donations: { canView: true, canCreate: true, canEdit: false, canDelete: false }
  }
};

/**
 * Helper function to get permissions for a user
 */
export function getUserPermissions(user: User | null, role?: Role | null): RolePermissions | null {
  if (!user) return null;
  
  // Super Admin always has full access
  if (user.role === 'super_admin') {
    return defaultPermissions.super_admin;
  }
  
  // Admin always has access to everything except Tenants (which is controlled separately)
  if (user.role === 'admin') {
    return defaultPermissions.admin;
  }
  
  // If user has a custom role assigned and role data is provided, use that
  if (user.roleId && role) {
    return role.permissions;
  }
  
  // Otherwise, fall back to default permissions based on role
  switch (user.role) {
    case 'manager':
      return defaultPermissions.manager;
    case 'user':
      return defaultPermissions.user;
    default:
      return defaultPermissions.user; // Safest default
  }
}

/**
 * Check if user can access dashboard
 */
export function canAccessDashboard(user: User | null, role?: Role | null): boolean {
  // All authenticated users should have access to the dashboard
  if (user) {
    // Super Admin and Admin always have dashboard access
    if (user.role === 'super_admin' || user.role === 'admin') {
      return true;
    }
    
    // For other roles, check permissions
    const permissions = getUserPermissions(user, role);
    return !!permissions?.dashboard;
  }
  return false;
}

/**
 * Check if user can access settings
 */
export function canAccessSettings(user: User | null, role?: Role | null): boolean {
  // Super admins and admins always have settings access
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return true;
  }
  
  // For custom roles, check the permissions
  const permissions = getUserPermissions(user, role);
  return !!permissions?.settings;
}

/**
 * Check if user can access users section
 */
export function canAccessUsers(user: User | null, role?: Role | null): boolean {
  // Super admins and admins always have users access
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return true;
  }
  
  // For custom roles, check permissions
  const permissions = getUserPermissions(user, role);
  return !!permissions?.users.canView;
}

/**
 * Check if user can access Virtual Terminal
 * Virtual Terminal is only accessible when explicitly enabled in role permissions
 */
export function canAccessVirtualTerminal(user: User | null, role?: Role | null): boolean {
  // Super admins and admins always have virtual terminal access
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return true;
  }
  
  // For other roles, check permissions
  const permissions = getUserPermissions(user, role);
  return !!permissions?.virtualTerminal;
}

/**
 * Get feature access for a specific module
 */
export function getFeatureAccess(
  user: User | null, 
  feature: keyof Omit<RolePermissions, 'dashboard' | 'settings' | 'virtualTerminal'>,
  role?: Role | null
): FeatureAccess {
  // Super admins and admins always have full access (except admin doesn't see tenants)
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return { canView: true, canCreate: true, canEdit: true, canDelete: true };
  }
  
  // For other roles, check permissions
  const permissions = getUserPermissions(user, role);
  if (!permissions) {
    return { canView: false, canCreate: false, canEdit: false, canDelete: false };
  }
  
  return permissions[feature] as FeatureAccess;
}

/**
 * Check if user has any level of access to a feature
 */
export function hasFeatureAccess(
  user: User | null, 
  feature: keyof Omit<RolePermissions, 'dashboard' | 'settings' | 'virtualTerminal'>,
  role?: Role | null
): boolean {
  // Super admins and admins always have access (except admin doesn't see tenants)
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return true;
  }
  
  // For other roles, check permissions
  const access = getFeatureAccess(user, feature, role);
  return access.canView;
}

/**
 * Check if user can perform a specific action on a feature
 * In the simplified model, if they can view the feature, they can perform all actions
 */
export function canPerformAction(
  user: User | null, 
  feature: keyof Omit<RolePermissions, 'dashboard' | 'settings' | 'virtualTerminal'>,
  action: keyof FeatureAccess,
  role?: Role | null
): boolean {
  // Super admins and admins always have permission
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return true;
  }
  
  // For other roles, check permissions
  const access = getFeatureAccess(user, feature, role);
  return access.canView; // If they can view, they can perform all actions
}

/**
 * Check if user is a company admin (built-in admin role or admin permissions)
 */
export function isCompanyAdmin(user: User | null, role?: Role | null): boolean {
  if (!user) return false;
  
  // Super admin and admin always have admin access
  if (user.role === 'super_admin' || user.role === 'admin') {
    return true;
  }
  
  // Check custom role permissions if role provided
  if (role) {
    // Admin requires settings access in the simplified model
    return !!role.permissions.settings;
  }
  
  return false;
}

/**
 * Enhanced admin permission checks for more consistent access control
 */

/**
 * Check if user is a super admin
 * @param user User object or null
 * @returns boolean indicating if user is a super admin
 */
export function isSuperAdmin(user: User | null): boolean {
  if (!user) return false;
  return user.role === 'super_admin';
}

/**
 * Check if user is an admin (includes super_admin)
 * @param user User object or null
 * @returns boolean indicating if user is an admin or super admin
 */
export function isAdmin(user: User | null): boolean {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'super_admin';
}

/**
 * Check if user is active
 * @param user User object or null
 * @returns boolean indicating if user is active
 */
export function isUserActive(user: User | null): boolean {
  if (!user) return false;
  return user.isActive === true;
}

/**
 * Check if user belongs to a specific company
 * @param user User object or null
 * @param companyId Company ID to check against
 * @returns boolean indicating if user belongs to the company
 */
export function belongsToCompany(user: User | null, companyId: string): boolean {
  if (!user) return false;
  return isSuperAdmin(user) || user.companyId === companyId;
}

/**
 * Check if user has access to a specific company's data
 * @param user User object or null
 * @param companyId Company ID to check against
 * @returns boolean indicating if user can access the company data
 */
export function hasCompanyAccess(user: User | null, companyId: string): boolean {
  if (!user) return false;
  return isSuperAdmin(user) || isAdmin(user) || user.companyId === companyId;
} 